import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Link, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { Modal } from 'synfrastructure';
import VideoPlayer from '../../../common/video/VideoPlayer';
import Icon from '../../../common/icons/Icon';
import Quiz from '../components/Quiz';
import DailyExtra from '../components/DailyExtra';
import { getLessonVideo, completeLesson, getQuiz, submitQuiz, resetQuiz } from '../redux/actions';
import {
  selectCourse,
  selectChapter,
  selectLesson,
  selectHasLessonAccess,
  selectHasChapterAccess,
} from '../redux/selectors';
import { videoProgress, completeVideo } from '../../redux/actions';

class Lesson extends Component {
  static propTypes = {
    canAccessChapter: PropTypes.bool.isRequired,
    canAccessLesson: PropTypes.bool.isRequired,
    course: PropTypes.object.isRequired,
    chapter: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    lesson: PropTypes.object.isRequired,
    quiz: PropTypes.object,
    quizResults: PropTypes.object,
    video: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  };

  state = {
    quizModalVisible: false,
    videoVisible: false,
  };

  componentDidMount() {
    const { dispatch, course, chapter, lesson, location } = this.props;

    this.autoplay = location.search === '?autoplay';

    if (lesson.quiz_id) {
      dispatch(getQuiz(lesson.quiz_id));
    }

    dispatch(getLessonVideo(course.id, chapter.id, lesson.id)).then(response => {
      if (this.autoplay && response.json) {
        this.setState({
          videoVisible: true,
        });
      }
    });
  }

  componentWillUpdate(nextProps) {
    if (this.props.lesson.quiz_id !== nextProps.lesson.quiz_id && nextProps.lesson.quiz_id) {
      this.props.dispatch(getQuiz(nextProps.lesson.quiz_id));
    }
  }

  componentWillUnmount() {
    this.props.dispatch(resetQuiz());
  }

  handleToggleVideo = e => {
    this.autoplay = true;
    if (e) {
      e.preventDefault();
    }
    this.setState(state => ({
      videoVisible: !state.videoVisible,
    }));
  };

  handleToggleQuiz = () => {
    this.setState(state => ({
      quizModalVisible: !state.quizModalVisible,
    }));
  };

  handleCompleteLesson = () => {
    const { dispatch, history, lesson } = this.props;
    dispatch(completeLesson(lesson.id)).then(() => {
      history.push(`./${lesson.id}/complete`);
    });
  };

  handleVideoEnd = () => {
    const { dispatch, lesson, video, quiz } = this.props;
    this.setState({
      videoVisible: false,
    });
    dispatch(completeVideo(video.get('id'))).then(response => {
      if (!lesson.quiz_id || quiz.get('passed')) {
        this.handleCompleteLesson();
      }
    });
  };

  handleQuizEnd = (id, answers) => {
    const { dispatch } = this.props;
    this.setState({
      quizModalVisible: false,
    });
    dispatch(submitQuiz(id, answers)).then(response => {
      if (response.json && response.json.passed) {
        this.handleCompleteLesson();
      }
    });
  };

  renderPostLessonContent = () => {
    const { course, lesson } = this.props;

    if (lesson.complete && lesson.daily_extra_id) {
      return (
        <div className="post-lesson">
          <div className="container">
            <DailyExtra dailyExtra={lesson.dailyExtra} />
            <div className="post-lesson-help">
              <h4>Need help with {course.name}?</h4>
              <h3>Book an Online Follow-up Today!</h3>
              <Link className="button" to="/subscription/coaching/online-follow-up">
                Book Now
              </Link>
            </div>
          </div>
        </div>
      );
    }
  };

  renderAction = () => {
    const { lesson, video, quiz, quizResults } = this.props;

    // Watched video, has quiz and results, quiz failed
    let quizLabel = 'Take Quiz';
    if (video.get('complete') && quiz && quizResults && !quizResults.get('passed')) {
      quizLabel = 'Retake Quiz';
    }

    // Watched video, has quiz
    if (video.get('complete') && quiz && !quiz.get('passed')) {
      return (
        <div className="quiz-modal">
          <Modal isVisible={this.state.quizModalVisible} onClose={this.handleToggleQuiz}>
            <div className="modal__content--alt">
              <Quiz
                quiz={quiz}
                videoId={lesson.video_id}
                title={`${lesson.name} | Quiz`}
                label={quizLabel}
                onSubmit={this.handleQuizEnd}
              />
            </div>
          </Modal>
          <button type="button" className="button button--large" onClick={this.handleToggleQuiz}>
            {quizLabel}
          </button>
        </div>
      );
    }

    // Watched video and has quiz and quiz passed previously,
    // or haven't watched video,
    // or have watched video and no quiz
    const initialTime = video.get('duration') - video.get('progress') < 5 ? 0 : video.get('progress');
    const playButtonLabel = initialTime > 0 ? 'Resume' : 'Play';

    return (
      <div>
        <button type="button" className="button button--large button--icon" onClick={this.handleToggleVideo}>
          <Icon icon="PlayCircle" /> {playButtonLabel} {lesson.name}
        </button>
      </div>
    );
  };

  renderHeroPlaceholder() {
    return (
      <div className="lesson__hero-wrapper lesson__hero-wrapper--placeholder">
        <div className="container">
          <Link to="#">
            <span className="button__back-button">
              <Icon icon="BackArrow" />Back to Chapter Overview
            </span>
          </Link>
          <div className="lesson-header">
            <div>
              <div className="lesson-header__video" />
            </div>
            <div className="lesson-header__info">
              <h2>course name</h2>
              <h1>lesson name</h1>
              <div className="lesson-header__time">2 minutes</div>
              <ul className="lesson-header__sections">
                <li className="lesson-header__section">section title</li>
                <li className="lesson-header__section">section title</li>
                <li className="lesson-header__section">section title</li>
              </ul>
              <button type="button" className="button button--large">
                button
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      canAccessLesson,
      canAccessChapter,
      course,
      chapter,
      dispatch,
      lesson,
      video,
      quiz,
      quizResults,
    } = this.props;

    if (!video || (lesson.quiz_id && !quiz)) {
      return this.renderHeroPlaceholder();
    }

    if (!canAccessChapter) {
      return <Redirect to={`/subscription/courses/${chapter.course_id}`} />;
    } else if (!canAccessLesson) {
      return <Redirect to={`/subscription/courses/${chapter.course_id}/chapters/${chapter.id}`} />;
    }

    const durationAsMinutes = Math.round(moment.duration(1000 * video.get('duration')).asMinutes());

    const classes = classNames({
      'lesson__hero-wrapper': true,
      'lesson__hero-wrapper__with-status': lesson.complete || (quizResults && !quizResults.get('passed')),
    });

    const titleString = `${course.name} - Chapter ${course.chapters.findIndex(item => item.id === chapter.id) +
      1} - ${lesson.name}`;

    return (
      <div className={classes}>
        <Helmet>
          <title>{titleString}</title>
        </Helmet>
        {lesson.complete ? (
          <div className="lesson__hero__status">
            <Icon icon="CheckmarkCircleOutline" />Congratulations on completing {lesson.name}
          </div>
        ) : quizResults && !quizResults.get('passed') ? (
          <div className="lesson__hero__status lesson__hero__status--failed">
            <Icon icon="CheckmarkCircleOutline" />You failed the quiz, please try again.
          </div>
        ) : null}
        <div className="container">
          <Link to={`/subscription/courses/${course.id}/chapters/${chapter.id}`}>
            <span className="button__back-button">
              <Icon icon="BackArrow" />Back to Chapter Overview
            </span>
          </Link>
          <div className="lesson-header">
            <div>
              <div
                className="lesson-header__video"
                style={{ backgroundImage: `url(${lesson.image.location})` }}
                onClick={this.handleToggleVideo}
              >
                <Icon icon="PlayCircle" />
              </div>
              {this.state.videoVisible && (
                <div className="fullscreen__video-player__wrapper">
                  <VideoPlayer
                    autoplay={this.autoplay}
                    key={`video-${lesson.id}`}
                    title={lesson.name}
                    poster={lesson.image.location}
                    onProgress={currentTime => {
                      dispatch(videoProgress(video.get('id'), currentTime));
                    }}
                    onEnd={this.handleVideoEnd}
                    sections={lesson.video_sections}
                    backText={`Back to ${lesson.name}`}
                    onBackTo={this.handleToggleVideo}
                    video={video}
                  />
                </div>
              )}
            </div>
            <div className="lesson-header__info">
              <h2>{course.name}</h2>
              <h1>{lesson.name}</h1>
              <div className="lesson-header__time">
                {durationAsMinutes === 1 ? '1 minute' : `${durationAsMinutes} minutes`}
              </div>
              <ul className="lesson-header__sections">
                {lesson.video_sections.map(section => (
                  <li className="lesson-header__section" key={section.startTime}>
                    {section.text}
                  </li>
                ))}
              </ul>
              {this.renderAction()}
            </div>
          </div>
        </div>
        {this.renderPostLessonContent()}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    canAccessLesson: selectHasLessonAccess(state, props),
    canAccessChapter: selectHasChapterAccess(state, props),
    course: selectCourse(state, props),
    chapter: selectChapter(state, props),
    lesson: selectLesson(state, props),
    video: state.subscription.courses.lessonVideo.get('data'),
    quiz: state.subscription.courses.quiz.get('data'),
    quizResults: state.subscription.courses.quizResults.get('data'),
  };
};

export default connect(mapStateToProps)(Lesson);



// WEBPACK FOOTER //
// ./src/subscription/courses/views/Lesson.js